import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Download } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

const STATUS_CLS = {
    PROCESSING: "bg-amber-100 text-amber-800",
    SUCCESS: "bg-emerald-100 text-emerald-800",
    FAILED: "bg-rose-100 text-rose-800",
    REVERSED: "bg-slate-200 text-slate-700",
};

export default function PanelPayouts() {
    const [payouts, setPayouts] = useState([]);
    const [err, setErr] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [search, setSearch] = useState("");

    const refresh = async () => {
        setErr("");
        try {
            const r = await api.get("/partner/payouts");
            setPayouts(r.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const filtered = useMemo(() => {
        return payouts.filter((p) => {
            if (from && new Date(p.created_at) < new Date(from)) return false;
            if (to && new Date(p.created_at) > new Date(`${to}T23:59:59`)) return false;
            if (search) {
                const s = search.toLowerCase();
                return (
                    (p.utr || "").toLowerCase().includes(s) ||
                    (p.payout_id || "").toLowerCase().includes(s) ||
                    (p.client_reference_id || "").toLowerCase().includes(s) ||
                    (p.beneficiary?.name || "").toLowerCase().includes(s) ||
                    (p.beneficiary?.account_number || "").toLowerCase().includes(s) ||
                    (p.beneficiary?.ifsc || "").toLowerCase().includes(s)
                );
            }
            return true;
        });
    }, [payouts, from, to, search]);

    const success = filtered.filter((p) => p.status === "SUCCESS");
    const totalSent = success.reduce((s, p) => s + p.amount, 0);
    const feesPaid = 0;

    const exportCsv = () => {
        const rows = [
            ["created_at", "payout_id", "ref", "beneficiary_name", "account_number", "ifsc", "amount", "status", "utr", "failure_reason"],
            ...filtered.map((p) => [
                p.created_at, p.payout_id, p.client_reference_id || "",
                p.beneficiary?.name || "", p.beneficiary?.account_number || "", p.beneficiary?.ifsc || "",
                p.amount, p.status, p.utr || "", p.failure_reason || "",
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `payouts-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="panel-payouts" className="p-6 space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Payout History</h1>
                    <p className="text-xs text-slate-500 mt-1">All payouts you have sent</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={refresh} data-testid="payouts-refresh"
                            className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                    <button onClick={exportCsv} data-testid="payouts-download"
                            className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" /> Download Excel
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">From</div>
                    <input data-testid="payouts-from" type="date" value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            className="input text-sm mt-1 w-full" />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">To</div>
                    <input data-testid="payouts-to" type="date" value={to}
                            onChange={(e) => setTo(e.target.value)}
                            className="input text-sm mt-1 w-full" />
                </div>
                <div className="flex-[2]">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Search</div>
                    <input data-testid="payouts-search" value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="UTR / order ID / beneficiary / account / IFSC"
                            className="input text-sm mt-1 w-full" />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <Stat label="Successful Payouts" value={success.length} sub="completed transactions" tone="emerald" testid="payouts-stat-success" />
                <Stat label="Total Amount Sent" value={fmtINR(totalSent)} sub="to beneficiaries" tone="emerald" testid="payouts-stat-total" />
                <Stat label="Fees Paid" value={fmtINR(feesPaid)} sub="payouts are free" tone="emerald" testid="payouts-stat-fees" />
            </div>

            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
                {filtered.length === 0 ? (
                    <p data-testid="payouts-empty" className="text-sm text-slate-400 text-center py-10">No payouts yet.</p>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2 text-left">When</th>
                                <th className="px-3 py-2 text-left">Order</th>
                                <th className="px-3 py-2 text-left">Beneficiary</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">UTR</th>
                                <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr key={p.payout_id} data-testid={`payouts-row-${p.payout_id}`} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{new Date(p.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono">
                                        <div>{p.payout_id}</div>
                                        <div className="text-[10px] text-slate-400">{p.client_reference_id || ""}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{p.beneficiary?.name}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{p.beneficiary?.account_number} · {p.beneficiary?.ifsc}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(p.amount)}</td>
                                    <td className="px-3 py-2 font-mono text-[11px]">{p.utr || "—"}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_CLS[p.status] || "bg-slate-100 text-slate-700"}`}>
                                            {p.status}
                                        </span>
                                        {p.failure_reason && <div className="text-[10px] text-rose-600 mt-0.5">{p.failure_reason}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, sub, tone, testid }) {
    const tones = {
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        blue: "bg-blue-50 border-blue-200 text-blue-900",
    };
    return (
        <div data-testid={testid} className={`${tones[tone]} border rounded-xl p-4`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
            <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
        </div>
    );
}
